import { atom } from "jotai";
import { DeductionFetchMode } from "@/core/deduction/FetchMode";
import { ResolveDeductionFetchModeUseCase } from "@/core/usecases/deduction";
import { quickFilterInfoAtom } from "./deductionFilter";
import { closeDeductionDrawerAtom, drawerInfoAtom, isDrawerOpenAtom } from "./drawer";
import { fetchDeductionListAtom } from "./main/main.actions";
import { groupByCheckAtom } from "./main/main.atoms";
import { refreshDeductionSourceQueuesAtom } from "./refreshDeductionSourceQueues.actions";
import { setCurrentPageAtom as setRemittanceCurrentPageAtom } from "./remittance/remittance.actions";
import { getDeductionCountAtom, setCurrentPageAtom as setRemittanceItemCurrentPageAtom } from "./remittanceItem/remittanceItem.actions";
import { remittanceItemsAtom } from "./remittanceItem/remittanceItem.atoms";

export interface RefreshDeductionWorkspaceOptions {
  refetch?: boolean;
  resetPage?: boolean;
}

const resolveRefreshOptions = (
  options: boolean | RefreshDeductionWorkspaceOptions = true
): Required<RefreshDeductionWorkspaceOptions> => {
  if (typeof options === "boolean") {
    return { refetch: options, resetPage: false };
  }

  return {
    refetch: options.refetch ?? true,
    resetPage: options.resetPage ?? false,
  };
};

/**
 * Single orchestrator for post-mutation refresh of the deductions workspace:
 * tab counts + active remittance/remittance-item table list.
 */
export const refreshDeductionWorkspaceAtom = atom(
  null,
  async (get, set, options: boolean | RefreshDeductionWorkspaceOptions = true) => {
    const { refetch, resetPage } = resolveRefreshOptions(options);

    if (resetPage) {
      const { statusId } = get(quickFilterInfoAtom);
      const groupByCheck = get(groupByCheckAtom);
      const mode = new ResolveDeductionFetchModeUseCase().execute({ statusId, groupByCheck });

      if (mode === DeductionFetchMode.RemittanceGrouped) {
        await set(setRemittanceCurrentPageAtom, 1);
      } else {
        await set(setRemittanceItemCurrentPageAtom, 1);
      }
    }

    await Promise.all([
      set(getDeductionCountAtom, refetch),
      set(fetchDeductionListAtom, refetch),
      set(refreshDeductionSourceQueuesAtom, { refetch }),
    ]);

    const isDrawerOpen = get(isDrawerOpenAtom);
    const drawerInfo = get(drawerInfoAtom);

    if (isDrawerOpen && drawerInfo?.isRemittanceItemsPage) {
      const remittanceItems = get(remittanceItemsAtom);
      const isSelectedItemInList = remittanceItems.some(
        item => item.remittanceItemId === drawerInfo.remittanceItemId
      );

      if (!isSelectedItemInList) {
        set(closeDeductionDrawerAtom);
      }
    }
  }
);
